import { Stack, type SxProps, type Theme } from "@mui/material";
import {
  gridRowNodeSelector,
  isAutogeneratedRow,
  useGridApiRef,
  type GridGroupingColDefOverride,
  type GridRenderCellParams,
  type GridRowId,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { use, useCallback, useEffect, useMemo, useState } from "react";
import type { ApiResponse } from "../../../api/types.ts";
import DataLoadingFailed from "../../../components/DataLoadingFailed.tsx";
import DataGrid from "../../../components/grid/DataGrid.tsx";
import DataGridGroupHeader from "../../../components/grid/DataGridGroupHeader.tsx";
import useFetch from "../../../hooks/useFetch.ts";
import useMergedSx from "../../../hooks/useMergedSx.ts";
import { logError } from "../../../logging.ts";
import { isDefined } from "../../../utilities/typeHelper.ts";
import GridSelectionContext from "./GridSelectionContext.ts";
import SelectionLimitContext from "./SelectionLimitContext.ts";
import { busySx, gridCheckboxColDef, singleSelectGridCheckboxColDef, toRowId } from "./filterGridHelper.tsx";
import SelectedItemsCount from "./SelectedItemsCount.tsx";
import {
  getSelectionUpToLimit,
  isSelectAllChecked,
  isSelectionWithinLimit,
  wouldExceedSelectionLimit,
} from "./selectionLimit.ts";
import type { TextFilterGridGrouping, TextFilterGridProps } from "./textFilterGridTypes.ts";

const DEFAULT_CONTAINER_MIN_HEIGHT = "255px";
const emptyRows: GridValidRowModel[] = [];
const selectionPropagation = { descendants: true, parents: true };
const isGroupExpandedByDefault = () => true;

const getSelectionModel = (
  rows: GridValidRowModel[],
  grouping: TextFilterGridGrouping,
  selectedRowIds: GridRowId[]
): GridRowSelectionModel => {
  const selectedValues = new Set(selectedRowIds);
  const ids = rows
    .filter((row) => {
      const value = row[grouping.leafField];
      return selectedValues.has(toRowId(value == null ? null : String(value)));
    })
    .map(grouping.getRowId);
  return { type: "include", ids: new Set(ids) };
};

interface Props extends TextFilterGridProps {
  grouping: TextFilterGridGrouping;
}

const GroupedTextFilterGrid = ({
  initialSorting,
  columns,
  loadRows,
  selectedRowIds,
  grouping,
  readonly,
  singleSelect,
  containerMinHeight,
  sx,
  onChanged,
}: Props) => {
  const maxSelectedValues = use(SelectionLimitContext);
  const apiRef = useGridApiRef();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(() => ({
    type: "include",
    ids: new Set(),
  }));
  const loadAllRows = useCallback(async (): Promise<ApiResponse<GridValidRowModel[]>> => {
    const range = { skip: 0, take: 0, totals: true };
    const result = await loadRows({
      allRows: true,
      range,
      sortModel: initialSorting,
    });
    return { success: true, data: result.rows };
  }, [initialSorting, loadRows]);

  const [result, fetchError, { isFetching }] = useFetch(loadAllRows);
  const rows = result ?? emptyRows;
  const selectedRowIdSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);

  const { rowsById, groupValuesByKey, allRowValues } = useMemo(() => {
    const rowsById = new Map<GridRowId, GridValidRowModel>();
    const groupValuesByKey = new Map<GridRowId, Set<GridRowId>>();
    const allRowValues = new Set<GridRowId>();
    for (const row of rows) {
      rowsById.set(grouping.getRowId(row), row);
      const groupKey = grouping.getGroupKey(row[grouping.groupField]);
      const groupValues = groupValuesByKey.get(groupKey) ?? new Set<GridRowId>();
      const value = row[grouping.leafField];
      const rowValue = toRowId(value == null ? null : String(value));
      groupValues.add(rowValue);
      allRowValues.add(rowValue);
      groupValuesByKey.set(groupKey, groupValues);
    }
    return { rowsById, groupValuesByKey, allRowValues };
  }, [grouping, rows]);

  useEffect(() => {
    const leafSelection = getSelectionModel(rows, grouping, selectedRowIds);
    const gridApi = apiRef.current;
    setSelectionModel(gridApi === null ? leafSelection : gridApi.getPropagatedRowSelectionModel(leafSelection));
  }, [apiRef, grouping, rows, selectedRowIds]);

  const groupedColumns = useMemo(
    () =>
      columns.map((column) =>
        column.field === grouping.groupField
          ? { ...column, groupingValueGetter: (value: unknown) => grouping.getGroupKey(value) }
          : column
      ),
    [columns, grouping]
  );
  const rowGroupingModel = useMemo(() => [grouping.groupField], [grouping.groupField]);
  const columnVisibilityModel = useMemo(
    () => ({ [grouping.groupField]: false, [grouping.leafField]: false }),
    [grouping.groupField, grouping.leafField]
  );
  const initialState = useMemo(() => ({ sorting: { sortModel: initialSorting } }), [initialSorting]);
  const gridContainerSx = useMemo(
    (): SxProps<Theme> => ({ minHeight: containerMinHeight ?? DEFAULT_CONTAINER_MIN_HEIGHT }),
    [containerMinHeight]
  );
  const groupingColDef = useMemo((): GridGroupingColDefOverride => {
    const leafColumn = columns.find((column) => column.field === grouping.leafField);
    const visibleDataColumnCount = columns.filter(
      (column) => column.field !== grouping.groupField && column.field !== grouping.leafField
    ).length;
    return {
      headerName: leafColumn?.headerName ?? grouping.leafField,
      leafField: grouping.leafField,
      hideDescendantCount: true,
      flex: 1,
      hideable: false,
      pinnable: false,
      renderHeader: (params) => <DataGridGroupHeader params={params} />,
      valueFormatter: (value, row) => (isAutogeneratedRow(row) ? grouping.getGroupLabel(value) : (value ?? "No Value")),
      colSpan: (_value, row) => (isAutogeneratedRow(row) ? visibleDataColumnCount + 1 : 1),
    };
  }, [columns, grouping]);

  const getLeafValue = useCallback(
    (rowId: GridRowId): GridRowId | undefined => {
      const row = rowsById.get(rowId);
      if (row === undefined) {
        return undefined;
      }
      const value = row[grouping.leafField];
      return toRowId(value == null ? null : String(value));
    },
    [grouping.leafField, rowsById]
  );

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      if (model.type === "exclude" || readonly === true) {
        return;
      }

      if (singleSelect === true) {
        setSelectionModel(model);
        onChanged([...new Set([...model.ids].map(getLeafValue).filter(isDefined<GridRowId>()))]);
        return;
      }

      const nextSelectedValues = new Set(selectedRowIds);
      const updateValue = (rowId: GridRowId, selected: boolean) => {
        const selectedValue = getLeafValue(rowId);
        if (selectedValue === undefined) {
          return;
        }
        if (selected) {
          nextSelectedValues.add(selectedValue);
        } else {
          nextSelectedValues.delete(selectedValue);
        }
      };

      model.ids.forEach((rowId) => {
        if (!selectionModel.ids.has(rowId)) {
          updateValue(rowId, true);
        }
      });
      selectionModel.ids.forEach((rowId) => {
        if (!model.ids.has(rowId)) {
          updateValue(rowId, false);
        }
      });

      if (!isSelectionWithinLimit(nextSelectedValues, maxSelectedValues)) {
        setSelectionModel({ type: "include", ids: new Set(selectionModel.ids) });
        return;
      }

      setSelectionModel(model);
      onChanged([...nextSelectedValues]);
    },
    [getLeafValue, maxSelectedValues, onChanged, readonly, selectedRowIds, selectionModel.ids, singleSelect]
  );

  const isGroupSelectable = useCallback(
    (groupingKey: unknown) => {
      const groupValues = groupValuesByKey.get(grouping.getGroupKey(groupingKey));
      return groupValues !== undefined && !wouldExceedSelectionLimit(selectedRowIdSet, groupValues, maxSelectedValues);
    },
    [groupValuesByKey, grouping, maxSelectedValues, selectedRowIdSet]
  );

  const isLeafRowSelectable = useCallback(
    (row: GridValidRowModel | undefined) => {
      if (row === undefined) {
        return false;
      }
      const value = row[grouping.leafField];
      return !wouldExceedSelectionLimit(
        selectedRowIdSet,
        [toRowId(value == null ? null : String(value))],
        maxSelectedValues
      );
    },
    [grouping.leafField, maxSelectedValues, selectedRowIdSet]
  );

  const isRowIdSelectable = useCallback(
    (rowId: GridRowId) => {
      const node = gridRowNodeSelector(apiRef, rowId);
      if (node?.type === "group") {
        return isGroupSelectable(node.groupingKey);
      }

      return isLeafRowSelectable(rowsById.get(rowId));
    },
    [apiRef, isGroupSelectable, isLeafRowSelectable, rowsById]
  );
  const isRowSelectable = useCallback((params: GridRowParams) => isRowIdSelectable(params.id), [isRowIdSelectable]);
  const isCellSelectionDisabled = useCallback(
    (params: GridRenderCellParams) => !isRowIdSelectable(params.id),
    [isRowIdSelectable]
  );

  const gridSx = useMergedSx(sx, busySx);
  const selectAllChecked = isSelectAllChecked(selectedRowIdSet, allRowValues, maxSelectedValues);
  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      const nextSelectedValues = checked ? getSelectionUpToLimit(selectedRowIds, allRowValues, maxSelectedValues) : [];
      const leafSelection = getSelectionModel(rows, grouping, nextSelectedValues);
      const gridApi = apiRef.current;
      setSelectionModel(gridApi === null ? leafSelection : gridApi.getPropagatedRowSelectionModel(leafSelection));
      onChanged(nextSelectedValues);
    },
    [allRowValues, apiRef, grouping, maxSelectedValues, onChanged, rows, selectedRowIds]
  );
  const gridSelectionState = useMemo(
    () => ({
      checked: selectAllChecked,
      indeterminate: result !== undefined && selectedRowIdSet.size > 0 && !selectAllChecked,
      onChange: handleSelectAllChange,
      isRowSelectionDisabled: isCellSelectionDisabled,
    }),
    [handleSelectAllChange, isCellSelectionDisabled, result, selectAllChecked, selectedRowIdSet.size]
  );

  if (fetchError !== undefined) {
    logError(fetchError, "[GroupedTextFilterGrid] loadRows");
    return <DataLoadingFailed title="Loading values failed" />;
  }

  return (
    <Stack spacing={0.5} sx={{ flex: 1 }}>
      <GridSelectionContext value={gridSelectionState}>
        <DataGrid
          containerSx={gridContainerSx}
          apiRef={apiRef}
          rows={rows}
          getRowId={grouping.getRowId}
          columns={groupedColumns}
          loading={isFetching}
          sx={gridSx}
          rowGroupingModel={rowGroupingModel}
          columnVisibilityModel={columnVisibilityModel}
          initialState={initialState}
          groupingColDef={groupingColDef}
          isGroupExpandedByDefault={isGroupExpandedByDefault}
          rowHeight={24}
          columnHeaderHeight={30}
          checkboxSelection={!readonly}
          checkboxColDef={singleSelect === true ? singleSelectGridCheckboxColDef : gridCheckboxColDef}
          disableMultipleRowSelection={singleSelect}
          isRowSelectable={isRowSelectable}
          disableRowSelectionOnClick={readonly}
          rowSelectionModel={selectionModel}
          rowSelectionPropagation={selectionPropagation}
          onRowSelectionModelChange={handleSelectionChange}
          disableRowSelectionExcludeModel
        />
      </GridSelectionContext>
      <SelectedItemsCount
        selectedCount={selectedRowIdSet.size}
        totalCount={allRowValues.size}
        limit={maxSelectedValues}
      />
    </Stack>
  );
};

export default GroupedTextFilterGrid;
